import { ModelTier } from './ChatBoxInput';
import {
  AgentItem,
  AgentGroup,
  AgentPreference,
  ProjectAgentCacheEntry,
  LOCKED_AGENT_IDS,
  AGENT_GROUPS,
  FIXED_MODEL_OPTIONS,
  HIGH_CAPABILITY_MODEL_OPTIONS,
  ALL_MODEL_OPTIONS,
} from './agentsModalTypes';

const projectAgentCache = new Map<string, ProjectAgentCacheEntry>();

export const getAgentModelOptions = (agentId: string): ModelTier[] | undefined => {
  const fixedModelTier = FIXED_MODEL_OPTIONS[agentId];

  if (fixedModelTier) {
    return [fixedModelTier];
  }

  if (agentId === 'implementation_agent') {
    return ALL_MODEL_OPTIONS;
  }

  return HIGH_CAPABILITY_MODEL_OPTIONS;
};

export const getDefaultModelTier = (agentId: string): ModelTier => {
  const fixedModelTier = FIXED_MODEL_OPTIONS[agentId];
  if (fixedModelTier) {
    return fixedModelTier;
  }
  return 'super_large';
};

export const createInitialState = (
  initialAgents?: Record<string, Partial<AgentPreference>>,
): Record<string, AgentPreference> => (
  AGENT_GROUPS.flatMap((group) => group.agents).reduce<Record<string, AgentPreference>>((acc, agent) => {
    const defaultModelTier = getDefaultModelTier(agent.id);
    const providedModelTier = initialAgents?.[agent.id]?.model_tier;

    // Validate that the provided model tier is actually allowed for this agent
    const allowedModelTiers = getAgentModelOptions(agent.id);
    const isModelValid = !!providedModelTier && (!allowedModelTiers || allowedModelTiers.includes(providedModelTier));

    acc[agent.id] = {
      enabled: LOCKED_AGENT_IDS.has(agent.id) ? true : (initialAgents?.[agent.id]?.enabled ?? true),
      model_tier: isModelValid ? (providedModelTier as ModelTier) : defaultModelTier,
    };
    return acc;
  }, {})
);

export const buildProjectAgentOverrides = (
  agents: Record<string, AgentPreference>,
): Record<string, AgentPreference> => {
  const defaults = createInitialState();

  return Object.fromEntries(
    Object.entries(agents).flatMap(([agentId, preference]) => {
      const defaultPreference = defaults[agentId];

      if (!defaultPreference) {
        return [];
      }

      if (
        preference.enabled === defaultPreference.enabled
        && preference.model_tier === defaultPreference.model_tier
      ) {
        return [];
      }

      return [[agentId, preference]];
    }),
  );
};

export const getCachedProjectAgents = (projectId?: string | null): ProjectAgentCacheEntry | null => {
  if (!projectId) {
    return null;
  }

  const cached = projectAgentCache.get(projectId);
  return cached ? { agents: createInitialState(cached.agents), version: cached.version } : null;
};

export const setCachedProjectAgents = (
  projectId: string,
  agents: Record<string, AgentPreference>,
) => {
  projectAgentCache.set(projectId, {
    agents,
    version: (projectAgentCache.get(projectId)?.version ?? 0) + 1,
  });
};

export const applyGlobalTaskConstraints = (
  taskAgents: Record<string, AgentPreference>,
  globalAgents: Record<string, AgentPreference>,
): Record<string, AgentPreference> => {
  const nextState = { ...taskAgents };

  Object.keys(nextState).forEach((agentId) => {
    if (globalAgents[agentId]?.enabled === false) {
      nextState[agentId] = { ...nextState[agentId], enabled: false };
    }
  });

  return nextState;
};

export const buildTaskAgentOverrides = (
  taskAgents: Record<string, AgentPreference>,
  projectAgents: Record<string, AgentPreference>,
): Record<string, Partial<AgentPreference>> => (
  Object.fromEntries(
    Object.entries(taskAgents).flatMap(([agentId, preference]) => {
      if (LOCKED_AGENT_IDS.has(agentId)) {
        return [];
      }

      const projectPreference = projectAgents[agentId];
      if (!projectPreference || projectPreference.enabled === false) {
        return [];
      }

      if (preference.enabled === projectPreference.enabled) {
        return [];
      }

      return [[agentId, { enabled: preference.enabled }]];
    }),
  )
);

export const countEnabledAgents = (agents: AgentItem[], enabledMap: Record<string, AgentPreference>): number => (
  agents.filter((agent) => enabledMap[agent.id]?.enabled).length
);

export const countRequiredAgents = (agents: AgentItem[]): number => (
  agents.filter((agent) => LOCKED_AGENT_IDS.has(agent.id)).length
);
